import { memo, type Dispatch, type SetStateAction } from 'react'
import { Link } from 'react-router'
import type { PaginationState, Row, SortingState } from '@tanstack/react-table'
import { createColumnHelper, flexRender, useTable } from '@tanstack/react-table'
import * as Mtds from '@mattilsynet/design/react'
import {
  ArrowRightIcon,
  HashIcon,
  InfoIcon,
  WarningIcon
} from '@phosphor-icons/react'
import {
  getSampleBusinessDisplayName,
  getResultStatus,
  getSampleStatus,
  hasSampleBeenCompromised,
  SampleResultStatus
} from '../../utils/sampleUtils'
import { getFirstRequisition } from '../../utils/requisitionUtils'
import { toLocaleDateString } from '../../utils/dateUtils'
import { SampleResultTag } from '../../components/sample-result-tag/SampleResultTag'
import { SampleStatusTag } from '../../components/sample-status-tag/SampleStatusTag'
import { SubstanceTags } from '../../components/substance-tags/SubstanceTags'
import type { Sample, Samples, SubSample } from '../../types/types'
import { SubSampleStatusDisplay } from '../../components/sub-sample-item/SubSampleItem'
import { EmptyState } from '../../components/empty-state/EmptyState'
import { groupedPaginatedTableFeatures } from '../tableFeatures'

type SampleExtended = Sample & {
  payload?: string
  subSample?: SubSample
}

export type SamplesTableProps = {
  data: Samples
  grouping?: string[]
  rowCount: number
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  hiddenColumnIds?: string[]
}

const isSubRow = (
  row: Row<typeof groupedPaginatedTableFeatures, SampleExtended>
): boolean => row.depth > 0

export const SamplesTable = memo(function SamplesTable({
  data,
  grouping,
  pagination,
  setPagination,
  sorting,
  setSorting,
  rowCount,
  hiddenColumnIds
}: Readonly<SamplesTableProps>) {
  const columnHelper = createColumnHelper<
    typeof groupedPaginatedTableFeatures,
    SampleExtended
  >()
  const columns = columnHelper.columns([
    columnHelper.accessor('id', {
      id: 'sampleId',
      header: 'Pose',
      cell: (info) => {
        if (isSubRow(info.row)) {
          return <span className='ml-2'>Del {info.row.original.id}</span>
        }
        return (
          <Link
            id={`sample-link-${info.row.original.id}`}
            to={`/samples/${info.row.original.id}`}
            aria-label='Gå til prøve'
            className='no-underline'
          >
            <Mtds.Flex data-align='center' data-gap='1'>
              <HashIcon />
              <strong>{info.getValue()}</strong>
            </Mtds.Flex>
          </Link>
        )
      }
    }),
    columnHelper.accessor('businessName', {
      header: 'Virksomhetsnavn',
      enableSorting: false,
      cell: (info) => {
        if (isSubRow(info.row)) {
          return ''
        }
        const displayName = getSampleBusinessDisplayName(info.row.original)
        return <strong>{displayName ?? '-'}</strong>
      }
    }),
    columnHelper.accessor('sampleType.product', {
      id: 'sampleType_product',
      header: 'Produkt',
      enableSorting: false,
      cell: (info) => (isSubRow(info.row) ? '' : (info.getValue() ?? '-'))
    }),
    columnHelper.accessor('sampleType.matrix', {
      id: 'sampleType_matrix',
      header: 'Prøve av',
      enableSorting: false,
      cell: (info) => (isSubRow(info.row) ? '' : (info.getValue() ?? '-'))
    }),
    columnHelper.display({
      id: 'requisition.analysisType',
      header: 'Analyser for',
      enableSorting: false,
      cell: (info) => {
        if (isSubRow(info.row)) return ''
        const requisition = getFirstRequisition(info.row.original)
        return (
          <SubstanceTags
            analysisType={requisition?.analysisType}
            showAllWhenFits={false}
          />
        )
      }
    }),
    columnHelper.accessor('takenDate', {
      header: 'Uttaksdato',
      enableSorting: true,
      cell: (info) =>
        isSubRow(info.row) ? '' : toLocaleDateString(info.getValue())
    }),
    columnHelper.accessor('sampntType.nameNo', {
      id: 'sampntType',
      header: 'Uttakssted',
      enableSorting: false,
      cell: (info) => (isSubRow(info.row) ? '' : (info.getValue() ?? '-'))
    }),
    columnHelper.accessor('state', {
      enableSorting: false,
      header: 'Status',
      cell: (info) =>
        isSubRow(info.row) ? (
          ''
        ) : (
          <SampleStatusTag status={getSampleStatus(info.row.original)} />
        )
    }),
    columnHelper.accessor('requisitions', {
      enableSorting: false,
      header: 'Resultat',
      cell: (info) => {
        const row = info.row
        const requisition = getFirstRequisition(info.row.original)

        if (isSubRow(row)) {
          const subSample = row.original.subSample
          const result = requisition?.results?.subSamples?.[row.original.id]
          if (subSample == null) return ''

          return (
            <SubSampleStatusDisplay
              result={result}
              isCompromised={subSample.compromised}
              complete={requisition?.results?.complete}
              sampleStatus={getSampleStatus(row.original)}
            />
          )
        }

        if (requisition == null) return '-'

        const isCompromised = hasSampleBeenCompromised(row.original)
        const status: SampleResultStatus | undefined = getResultStatus(
          requisition.results,
          isCompromised
        )
        const followUp = requisition.followUp
        const hasProcessingError = requisition.latestProcessingError != null
        const result =
          status == null ? '-' : <SampleResultTag status={status} />

        return (
          <Mtds.Flex data-nowrap data-align='center' data-gap='2'>
            {result}
            {followUp === true && (
              <Mtds.Tag data-color='warning'>Oppfølging</Mtds.Tag>
            )}
            {hasProcessingError && (
              <Mtds.Tag data-color='warning'>
                <WarningIcon aria-hidden />
                Feil under behandling
              </Mtds.Tag>
            )}
          </Mtds.Flex>
        )
      }
    }),
    columnHelper.display({
      id: 'subSamples',
      header: 'Delprøver',
      cell: (info) => {
        if (isSubRow(info.row)) return ''
        const subSamples = info.row.original.subSamples ?? []
        const canExpand = info.row.getCanExpand() && subSamples.length > 1
        if (!canExpand) return '-'
        return (
          <Mtds.Button
            aria-expanded={info.row.getIsExpanded()}
            data-arrow
            data-size='sm'
            data-variant='tertiary'
            onClick={info.row.getToggleExpandedHandler()}
          >
            {`${subSamples.length} delprøver`}
          </Mtds.Button>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => {
        if (isSubRow(info.row)) return <InfoIcon data-size='lg' />

        return (
          <Mtds.Flex data-justify='end'>
            <ArrowRightIcon data-size='lg' />
          </Mtds.Flex>
        )
      }
    })
  ])

  const columnVisibility = (hiddenColumnIds ?? []).reduce<
    Record<string, boolean>
  >((acc, id) => {
    acc[id] = false
    return acc
  }, {})

  const table = useTable({
    features: groupedPaginatedTableFeatures,
    data,
    rowCount,
    columns,
    manualSorting: true,
    state: {
      grouping,
      sorting,
      pagination,
      columnVisibility
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    getSubRows: (sample) =>
      [...(sample.subSamples ?? [])]
        .sort((a, b) => a.subSampleId - b.subSampleId)
        .map((subSample) => ({
          ...sample,
          id: subSample.subSampleId.toString(),
          subSamples: undefined,
          payload: subSample.payload,
          subSample
        }))
  })

  if (data.length === 0) {
    return (
      <EmptyState title='Ingen prøver' description='Ingen prøver å vise.' />
    )
  }

  return (
    <Mtds.Grid data-gap='10'>
      <figure className='overflow-x-auto'>
        <Mtds.Table data-align='center' aria-label='Prøver' data-nowrap>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Mtds.Table.ThSortable
                    id={header.id}
                    key={header.id}
                    aria-sort={header.column.getIsSorted()}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    data-cy={header.id}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Mtds.Table.ThSortable>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                data-cy={row.id}
                data-clickdelegatefor={
                  isSubRow(row) ? undefined : `sample-link-${row.original.id}`
                }
                className={
                  isSubRow(row)
                    ? 'bg-neutral-surface-tinted'
                    : 'hover:bg-surface-hover cursor-pointer'
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    data-numeric={
                      cell.column.id === 'takenDate' ? true : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Mtds.Table>
      </figure>
      <Mtds.Flex data-align='center' data-justify='space-between' data-wrap>
        <p>
          Viser {table.getRowModel().rows.length} av {rowCount}
        </p>
        <Mtds.Pagination
          current={table.state.pagination.pageIndex + 1}
          total={table.getPageCount()}
          show={table.state.pagination.pageSize}
          props={({ page }) => ({
            onClick: () => {
              globalThis.scrollTo({ top: 0 })
              table.setPageIndex(page - 1)
            }
          })}
        />
        <Mtds.Field
          as='select'
          value={table.state.pagination.pageSize.toString() || '20'}
          onChange={({ target }) => table.setPageSize(Number(target.value))}
          options={[
            { value: '10', label: 'Vis 10 pr. side' },
            { value: '20', label: 'Vis 20 pr. side' },
            { value: '50', label: 'Vis 50 pr. side' },
            { value: '100', label: 'Vis 100 pr. side' },
            { value: '200', label: 'Vis 200 pr. side' }
          ]}
        />
      </Mtds.Flex>
    </Mtds.Grid>
  )
})
